import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

let faceLandmarker: FaceLandmarker | null = null;
let isLoading = false;
let loadPromise: Promise<FaceLandmarker> | null = null;

/**
 * Initialize and load the MediaPipe FaceLandmarker model.
 * Uses a singleton pattern to ensure the model is only loaded once.
 *
 * @returns Promise resolving to the FaceLandmarker instance
 */
export async function loadFaceMesh(): Promise<FaceLandmarker> {
  // Return existing instance if already loaded
  if (faceLandmarker) {
    return faceLandmarker;
  }

  // Return existing promise if currently loading
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start loading
  isLoading = true;
  loadPromise = (async () => {
    try {
      // Load the vision fileset from CDN
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm"
      );

      // Create FaceLandmarker with configuration
      const landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          delegate: "GPU", // Use GPU acceleration if available
        },
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false,
        runningMode: "VIDEO",
        numFaces: 1, // Only detect one face (local player)
      });

      faceLandmarker = landmarker;
      isLoading = false;
      return landmarker;
    } catch (error) {
      isLoading = false;
      loadPromise = null;
      console.error("Failed to load FaceLandmarker:", error);
      throw error;
    }
  })();

  return loadPromise;
}

/**
 * Get the current FaceLandmarker instance if loaded, or null
 */
export function getFaceLandmarker(): FaceLandmarker | null {
  return faceLandmarker;
}

/**
 * Clean up and dispose of the FaceLandmarker instance
 */
export function disposeFaceMesh(): void {
  if (faceLandmarker) {
    faceLandmarker.close();
    faceLandmarker = null;
  }
  isLoading = false;
  loadPromise = null;
}
